import type { PortfolioReport } from '@interfaces/portfolioReport';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from '@ovComponents/2-component/accordion/accordion';
import { Badge } from '@ovComponents/2-component/badge/badge';
import { formatMoneyValue } from '@utils/moneyFormatting';
import { Box, Typography } from 'hephaestus/web';
import { ExpandMore as ExpandMoreIcon } from 'hephaestus/webIcons';
import type { TFunction } from 'i18next';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { PortfolioAdvisorDecisionInsight } from '../../types/advisorInsights.types';
import type { OptimizerAuditV2Record } from '../../types/audit.types';
import type { WeightDiffDetailRow } from '../../types/snapshot.types';
import { isFundingSegregationNotApplied } from '../../utils/audit/decisionPredicates';
import {
  AdvisorSituationKey,
  isCashSituationDuplicatedInChanges,
  OptimizerDecisionImpact,
  PORTFOLIO_CASH_SITUATION_KEYS,
  PORTFOLIO_SITUATION_DECISION_STAGES_HIDDEN,
} from '../../utils/insights/advisorSituationKeys';
import { buildPortfolioAdvisorInsights } from '../../utils/insights/buildPortfolioAdvisorInsights';
import {
  buildPortfolioRebalanceInsightAiContext,
  canGeneratePortfolioInsightNarrative,
} from '../../utils/insights/buildPortfolioRebalanceInsightAiContext';
import {
  buildRebalanceChangeSummary,
  type RebalanceChangeSummary,
} from '../../utils/insights/buildRebalanceChangeSummary';
import { buildTaxLocationAccountFlows } from '../../utils/insights/buildTaxLocationAccountFlows';
import {
  buildSubAccountNamesById,
  formatAdvisorDecisionAction,
  formatAdvisorDecisionStage,
  resolveAdvisorSituationKey,
} from '../../utils/insights/formatAdvisorInsightText';
import {
  buildPortfolioInsightsCollapsedPreview,
  formatPortfolioTradeMixMeterCopy,
  shouldShowPortfolioTurnover,
} from '../../utils/presentation/formatRunImpactSummary';
import type { ReportSubAccountDisplayMetadata } from '../../utils/snapshot/buildReportDisplayMetadata';
import {
  BulkRebalanceInsightAiGenerateButton,
  BulkRebalanceInsightAiNarrative,
  BulkRebalanceInsightAiResult,
} from '../shared/insightAiNarrative';
import {
  InsightMetricCard,
  InsightMetricsRow,
  InsightSectionLabel,
  InsightTaxSellSplitBar,
  InsightTradeMixMeter,
  InsightTurnoverMeter,
} from '../shared/insightVisuals';

export type BulkRebalanceAdvisorInsightsAccordionProps = {
  audit?: OptimizerAuditV2Record;
  embedded?: boolean;
  subAccountsById?: Map<string, ReportSubAccountDisplayMetadata>;
  report?: PortfolioReport;
  diffRows?: WeightDiffDetailRow[];
  optimizerRunId?: string;
  clientLabel?: string;
  goalLabel?: string;
  unreliable?: boolean;
};

const isHiddenSituationDecision = (
  decision: PortfolioAdvisorDecisionInsight,
  changes: RebalanceChangeSummary,
) => {
  if (PORTFOLIO_SITUATION_DECISION_STAGES_HIDDEN.has(decision.stage)) {
    return true;
  }

  if (isFundingSegregationNotApplied(decision)) {
    return true;
  }

  const situationKey = resolveAdvisorSituationKey(decision);
  if (!PORTFOLIO_CASH_SITUATION_KEYS.has(situationKey)) {
    return false;
  }

  return isCashSituationDuplicatedInChanges(situationKey, changes);
};

const getDecisionBadgeColor = (impact: string) => {
  if (impact === OptimizerDecisionImpact.Blocking) {
    return 'negative' as const;
  }

  return 'warning' as const;
};

const tickerIsMentionedInText = (ticker: string, text: string) => {
  if (!ticker || !text) {
    return false;
  }

  const escaped = ticker.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(`\\b${escaped}\\b`, 'i').test(text);
};

const tickersNotMentionedInText = (tickers: string[], text: string) =>
  tickers.filter((ticker) => !tickerIsMentionedInText(ticker, text));

const formatCashRaiseChangeLine = (
  changes: RebalanceChangeSummary,
  cashRaiseAction: string | undefined,
  t: TFunction,
): string => {
  const value = formatMoneyValue(changes.displayCashRaiseCents);

  if (changes.cashRaiseCents > 0) {
    return t('insights.whyRaisingCash', { value });
  }

  return cashRaiseAction ?? t('insights.whyRaisingCash', { value });
};

const formatCashDeployChangeLine = (
  changes: RebalanceChangeSummary,
  cashDeployAction: string | undefined,
  t: TFunction,
): string => {
  if (cashDeployAction) {
    return cashDeployAction;
  }

  const key =
    changes.cashDeployCents > 0
      ? 'insights.whyDeployingCash'
      : 'insights.whyDeployedCashOnSleeve';

  return t(key, {
    value: formatMoneyValue(changes.displayCashDeployCents),
  });
};

/** Pills of tickers entering or leaving the sleeves. */
const TickerPills = ({
  tickers,
  color,
}: {
  tickers: string[];
  color: 'positive' | 'negative';
}) => (
  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
    {tickers.map((ticker) => (
      <Badge key={`${color}-${ticker}`} label={ticker} color={color} />
    ))}
  </Box>
);

export const BulkRebalanceAdvisorInsightsAccordion = ({
  audit,
  embedded = false,
  subAccountsById = new Map(),
  report,
  diffRows = [],
  optimizerRunId,
  clientLabel,
  goalLabel,
  unreliable = false,
}: BulkRebalanceAdvisorInsightsAccordionProps) => {
  const { t } = useTranslation(['bulkRebalance']);
  const [expanded, setExpanded] = useState(false);

  const resolvedSubAccountsById = useMemo(
    () => buildSubAccountNamesById(subAccountsById, audit),
    [audit, subAccountsById],
  );

  const insights = useMemo(
    () => buildPortfolioAdvisorInsights(audit, report),
    [audit, report],
  );
  const taxLocationFlows = useMemo(
    () => buildTaxLocationAccountFlows(audit, resolvedSubAccountsById),
    [audit, resolvedSubAccountsById],
  );
  const changes = useMemo(
    () => buildRebalanceChangeSummary(report, diffRows, audit),
    [audit, diffRows, report],
  );

  const summaryParts = useMemo(() => {
    if (!insights) {
      return [];
    }

    return buildPortfolioInsightsCollapsedPreview(
      insights.taxInsight,
      insights.turnoverPct,
      changes,
      t,
    );
  }, [changes, insights, t]);

  const tradeMixCopy = useMemo(
    () =>
      formatPortfolioTradeMixMeterCopy(
        audit,
        changes,
        insights?.turnoverPct,
        t,
        insights?.taxInsight,
      ),
    [audit, changes, insights?.taxInsight, insights?.turnoverPct, t],
  );

  const portfolioReportId = report?.id ?? audit?.portfolioReportId ?? undefined;
  const showAi =
    !!optimizerRunId &&
    !!portfolioReportId &&
    canGeneratePortfolioInsightNarrative(audit, unreliable);
  const aiInput = useMemo(
    () =>
      optimizerRunId && portfolioReportId
        ? {
            scope: 'PORTFOLIO' as const,
            optimizerRunId,
            portfolioReportId,
            context: buildPortfolioRebalanceInsightAiContext({
              audit,
              report,
              diffRows,
              subAccountsById: resolvedSubAccountsById,
              clientLabel,
              goalLabel,
              t,
            }),
          }
        : null,
    [
      audit,
      clientLabel,
      diffRows,
      goalLabel,
      optimizerRunId,
      portfolioReportId,
      report,
      resolvedSubAccountsById,
      t,
    ],
  );

  if (!insights) {
    return null;
  }

  const { taxInsight, turnoverBreakdown, decisions } = insights;
  const usedCashModeSubAccountIds = new Set<string>();
  const formattedDecisions = decisions.map((decision) => ({
    decision,
    situationKey: resolveAdvisorSituationKey(decision),
    actionLabel: formatAdvisorDecisionAction(
      decision,
      resolvedSubAccountsById,
      audit,
      t,
      { diffRows, usedCashModeSubAccountIds },
    ),
  }));
  const situationDecisions = formattedDecisions.filter(
    ({ decision }) => !isHiddenSituationDecision(decision, changes),
  );
  const cashRaiseAction = formattedDecisions.find(
    ({ situationKey }) => situationKey === AdvisorSituationKey.CashRaise,
  )?.actionLabel;
  const cashDeployAction = formattedDecisions.find(
    ({ situationKey }) => situationKey === AdvisorSituationKey.CashDeploy,
  )?.actionLabel;
  const cashRaiseLine =
    changes.displayCashRaiseCents > 0
      ? formatCashRaiseChangeLine(changes, cashRaiseAction, t)
      : undefined;
  const cashDeployLine =
    changes.displayCashDeployCents > 0
      ? formatCashDeployChangeLine(changes, cashDeployAction, t)
      : undefined;
  const visibleCashStoryText = [cashRaiseLine, cashDeployLine]
    .filter(Boolean)
    .join(' ');
  const newPositions = tickersNotMentionedInText(
    changes.newPositions,
    visibleCashStoryText,
  );
  const exits = tickersNotMentionedInText(changes.exits, visibleCashStoryText);
  const showPortfolioTurnover = shouldShowPortfolioTurnover(
    insights.turnoverPct,
    changes.isCashEvent,
  );
  const showChangeDetails =
    changes.newPositions.length > 0 ||
    changes.exits.length > 0 ||
    changes.displayCashRaiseCents > 0 ||
    changes.displayCashDeployCents > 0 ||
    changes.hasMixedSleeveCash;
  const showChangesColumn = showChangeDetails || changes.tradedHoldingCount > 0;
  const showMetrics =
    !!taxInsight ||
    (showPortfolioTurnover && !!turnoverBreakdown) ||
    tradeMixCopy != null ||
    taxLocationFlows.length > 0;

  const accordion = (
    <Accordion
      variant='outlined'
      disableGutters
      expanded={expanded}
      onChange={(_, nextExpanded) => setExpanded(nextExpanded)}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon fontSize='small' />}
        data-testid='bulk-rebalance-advisor-insights-summary'
        sx={{
          '& .MuiAccordionSummary-content': {
            width: '100%',
            mr: 1,
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            width: '100%',
            pr: 1,
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
              minWidth: 0,
            }}
          >
            <Typography variant='bodyMedium' sx={{ fontWeight: 600 }}>
              {t('insights.title')}
            </Typography>
            <Typography variant='labelSmall' color='textSecondary'>
              {summaryParts.join(' · ')}
            </Typography>
          </Box>
          {showAi ? <BulkRebalanceInsightAiGenerateButton /> : null}
        </Box>
      </AccordionSummary>

      <AccordionDetails>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
          {showChangesColumn ? (
            <Box
              data-testid='bulk-rebalance-advisor-changes'
              sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}
            >
              <InsightSectionLabel>
                {t('insights.changesSectionTitle')}
              </InsightSectionLabel>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                {(changes.displayCashRaiseCents > 0 ||
                  changes.displayCashDeployCents > 0) && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.25,
                    }}
                  >
                    {changes.displayCashRaiseCents > 0 ? (
                      <Typography variant='bodySmall'>
                        {cashRaiseLine}
                      </Typography>
                    ) : null}
                    {changes.displayCashDeployCents > 0 ? (
                      <Typography variant='bodySmall'>
                        {cashDeployLine}
                      </Typography>
                    ) : null}
                  </Box>
                )}

                {newPositions.length > 0 ? (
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 0.75,
                      alignItems: 'center',
                    }}
                  >
                    <Typography variant='labelSmall' color='textSecondary'>
                      {t('insights.newPositionsLabel')}
                    </Typography>
                    <TickerPills tickers={newPositions} color='positive' />
                  </Box>
                ) : null}

                {exits.length > 0 ? (
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 0.75,
                      alignItems: 'center',
                    }}
                  >
                    <Typography variant='labelSmall' color='textSecondary'>
                      {changes.isCashEvent
                        ? t('insights.soldToRaiseCashLabel')
                        : t('insights.exitedLabel')}
                    </Typography>
                    <TickerPills tickers={exits} color='negative' />
                  </Box>
                ) : null}

                {!showChangeDetails && changes.tradedHoldingCount > 0 ? (
                  <Typography variant='bodySmall' color='textSecondary'>
                    {t('insights.broughtToTarget', {
                      count: changes.tradedHoldingCount,
                    })}
                  </Typography>
                ) : null}
              </Box>
            </Box>
          ) : null}

          {showMetrics ? (
            <Box data-testid='bulk-rebalance-advisor-metrics'>
              <InsightMetricsRow>
                {taxInsight ? (
                  <InsightMetricCard
                    title={t('runInsights.impact.taxTitle')}
                    framed={false}
                    testId='bulk-rebalance-advisor-tax-insight'
                  >
                    <InsightTaxSellSplitBar
                      registeredSellCents={taxInsight.registeredSellCents}
                      taxableSellCents={taxInsight.taxableSellCents}
                      estRealizedGainCents={taxInsight.estRealizedGainCents}
                      estRealizedGainIsComplete={
                        taxInsight.estRealizedGainIsComplete
                      }
                      incompleteGainLineCount={
                        taxInsight.incompleteGainLineCount
                      }
                      testId='bulk-rebalance-advisor-tax-split'
                    />
                    {taxLocationFlows.length > 0 ? (
                      <Box
                        data-testid='bulk-rebalance-tax-location-flows'
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.5,
                          mt: 0.5,
                        }}
                      >
                        {taxLocationFlows.map((flow) => (
                          <Typography
                            key={`${flow.fromAccount}-${flow.toAccount}`}
                            variant='labelSmall'
                            color='textSecondary'
                          >
                            {t('insights.taxLocationFlow', {
                              value: formatMoneyValue(flow.amountCents),
                              from: flow.fromAccount,
                              to: flow.toAccount,
                            })}
                          </Typography>
                        ))}
                      </Box>
                    ) : null}
                  </InsightMetricCard>
                ) : null}

                {showPortfolioTurnover && turnoverBreakdown ? (
                  <InsightMetricCard
                    title={t('insights.turnoverSectionTitle')}
                    framed={false}
                    testId='bulk-rebalance-advisor-turnover-insight'
                  >
                    <InsightTurnoverMeter
                      turnoverPct={turnoverBreakdown.turnoverPct}
                      buyPct={turnoverBreakdown.buyPct}
                      sellPct={turnoverBreakdown.sellPct}
                      holdingCount={turnoverBreakdown.holdingCount}
                      testId='bulk-rebalance-advisor-turnover-meter'
                    />
                  </InsightMetricCard>
                ) : null}

                {tradeMixCopy ? (
                  <InsightMetricCard
                    title={t('runInsights.impact.tradeMixTitle')}
                    framed={false}
                    testId='bulk-rebalance-advisor-trade-mix-insight'
                  >
                    <InsightTradeMixMeter
                      buyCents={tradeMixCopy.buyCents}
                      sellCents={tradeMixCopy.sellCents}
                      centerLabel={tradeMixCopy.centerLabel}
                      caption={tradeMixCopy.caption}
                      footnote={tradeMixCopy.footnote}
                      testId='bulk-rebalance-advisor-trade-mix-meter'
                    />
                  </InsightMetricCard>
                ) : null}
              </InsightMetricsRow>
            </Box>
          ) : null}

          {situationDecisions.length > 0 ? (
            <Box data-testid='bulk-rebalance-advisor-situations-insight'>
              <InsightSectionLabel>
                {t('insights.situationsSectionTitle')}
              </InsightSectionLabel>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {situationDecisions.map(({ decision, actionLabel }) => (
                  <Box
                    key={`${decision.stage}-${decision.action}`}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'auto minmax(0, 1fr)',
                      columnGap: 1,
                      alignItems: 'center',
                    }}
                  >
                    <Badge
                      label={formatAdvisorDecisionStage(decision, t)}
                      color={getDecisionBadgeColor(decision.impact)}
                      sx={{ flexShrink: 0 }}
                    />
                    <Typography variant='bodySmall' color='textSecondary'>
                      {actionLabel}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          ) : null}

          {showAi ? <BulkRebalanceInsightAiResult /> : null}
        </Box>
      </AccordionDetails>
    </Accordion>
  );

  return (
    <Box
      data-testid='bulk-rebalance-advisor-insights-accordion'
      sx={{
        pt: embedded ? 1 : 0,
        pb: embedded ? 0 : 0.5,
        px: embedded ? 0 : 2,
      }}
    >
      {showAi && aiInput ? (
        <BulkRebalanceInsightAiNarrative
          scope='portfolio'
          input={aiInput}
          onGenerateStart={() => setExpanded(true)}
        >
          {accordion}
        </BulkRebalanceInsightAiNarrative>
      ) : (
        accordion
      )}
    </Box>
  );
};
